import type { MarketDataSnapshotRequest } from '@talos/kyoko';
import { useBehaviorSubject, useConstant, useMultiMarketDataSnapshot } from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import type { FixingState } from './fixingSlice';

/** Given the current fixing state, will spin up the necesesary MDS streams and return the (combined) data observable from those */
export const useMarketDataForFixingState = (state: FixingState) => {
  const { fixingPriceDialogMarkets } = useFeatureFlag();
  const marketDataSnapshotMarkets = useMemo(() => fixingPriceDialogMarkets.split(','), [fixingPriceDialogMarkets]);

  const symbols = useMemo(() => compact(state.fixings.map(fixing => fixing.symbol)), [state.fixings]);
  const requests = useMemo(() => symbols.map(symbol => ({ key: symbol, Symbol: symbol })), [symbols]);
  const { observable: requestsObs } = useBehaviorSubject(() => requests, [requests]);

  const requestOverrides: Partial<MarketDataSnapshotRequest> = useConstant({
    Depth: 1,
    Throttle: '10s',
    Markets: marketDataSnapshotMarkets,
  });

  return useMultiMarketDataSnapshot({
    requestsObs,
    // arbitrarily high number so we dont hit any issues with this number flip-flopping
    // we dont have any indexes with this many legs. When we do, we will probably stop making this sub entirely. So this is fine for now
    maxActiveSubscriptions: 50,
    subscriptionTag: 'ApplyFixingDialog-MultiMDS',
    requestOverrides,
  });
};
